/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import IDependency from './IDependency';
import StateCompletionManager from './StateCompletionManager';
import StateComponent from './StateComponent';
import StateEvent from './StateEvent';
import StateReference from './StateReference';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** cc.Component used as dependency on [[StateComponent]] */
@cc._decorator.ccclass
export default abstract class StateDependencyComponent extends ReferencedComponent implements IDependency
{
    protected stateDependencies: [StateComponent, boolean][] = [];

    /** Virtual function for handling state change. */
    @Decorators.BindThis()
    protected onStateChangeHandler = (event: StateEvent): void => { return; }

    /** Manager for tracking complete flags on multiple states. */
    private _stateCompletionManager: StateCompletionManager;

    /** Name of this node. */
    public NodeName: string = "";

    @property()
    protected onStateChangeDelay: number = 0;

    /**
     * State Life-Cycle
     * @param refs [[StateReference]]s for which this dependency will be added to.
     */
    protected start(...refs: (StateReference | StateReference[])[]): void
    {
        super.start();

        if (refs) { this.addStateDependencyRefs(...refs); }

        this.NodeName = this.node.name;

        const stateComponents: StateComponent[] = this.stateDependencies.map(dependency => dependency[0]);

        this._stateCompletionManager = new StateCompletionManager(stateComponents);
        this.bindListeners();
    }

    /** @hidden */
    protected update(dt: number): void { super.update(dt); }

    /** @hidden */
    protected onDestroy(): void
    {
        this.unbindListeners();
        super.onDestroy();
    }

    /**
     * Sets completion status for a givent state.
     * @param state State to set completion of.
     * @returns This [[StateDependencyComponent]].
     */
    protected completeState = (state: StateComponent): StateDependencyComponent =>
    {
        if (!this._stateCompletionManager.Has(state))
        {
            CLog.error(`State does not exist.`, this, LogTypes.StateManager);
            return this;
        }

        this._stateCompletionManager.SetStateCompleted(state);
        return this;
    }

    /**
     * Set completion status to optional for a given state.
     * @param state State to set optional completion of.
     * @returns This [[StateDependencyComponent]].
     */
    protected completeStateOptionalNext = (state: StateComponent): StateDependencyComponent =>
    {
        state.IsOptionalNext = true;
        return this.completeState(state);
    }

    /**
     * Local helper for adding references of states for [[StateCompletionManager]].
     * @param refs [[StateReference]] arrays for managing completion.
     */
    private addStateDependencyRefs(...refs: (StateReference | StateReference[])[]): void
    {
        refs.forEach(stateReferenceArray =>
        {
            if (stateReferenceArray instanceof StateReference)
            {
                let reference: StateReference = stateReferenceArray as StateReference;
                this.stateDependencies.push([reference.Value, reference.Blocking]);
                return;
            }

            (stateReferenceArray as StateReference[]).forEach(stateReference =>
            {
                this.stateDependencies.push([stateReference.Value, stateReference.Blocking]);
            });
        }, this);
    }

    /** Binds dependency to onStateChangeHandler for component. */
    private bindListeners(): StateDependencyComponent
    {
        this.stateDependencies
            .forEach(stateInfo =>
            {
                const stateComponent: StateComponent = stateInfo[0];
                if (!stateComponent) { return; }

                const isDependency: boolean = stateInfo[1];

                if (this.onStateChangeDelay > 0)
                {
                    stateComponent.OnStateChange((event: StateEvent) =>
                        {
                            this.Reset();
                            this.scheduleOnce(this.onStateChangeHandler.bind(this, event),
                                this.onStateChangeDelay / 1000);
                        });
                }
                else
                {
                    stateComponent.OnStateChange(this.onStateChangeHandler);
                }

                if (isDependency) { stateComponent.AddDependency(this); }

                if (this.onStateChangeHandler && !stateComponent.StateManager)
                {
                    CLog.log('No stateManager on dependency.', LogTypes.StateManager, this);
                }
            }, this);

        return this;
    }

    /** Removes listeners. */
    private unbindListeners(): StateDependencyComponent
    {
        this.stateDependencies
            .forEach(stateInfo =>
            {
                const state: StateComponent = stateInfo[0];
                if (!state) { return; }
                state.OffStateChange(this.onStateChangeHandler);

                if (stateInfo[1])
                {
                    state.RemoveDependency(this);
                }
            }, this);

        return this;
    }

    // #region IDependency

    /**
     * Get completion status for given state.
     * @param state [[StateComponent]] to check completeness status.
     * @returns True if given dependency complete for given state.
     */
    public IsComplete(state: StateComponent): boolean
    {
        if (!this._stateCompletionManager)
        {
            CLog.error('No StateCompletionManager configured.', this, LogTypes.StateManager);
            return true;
        }
        return this._stateCompletionManager.IsStateCompleted(state);
    }

    /**
     * Sets all state completion status' to false.
     * @return This [[StateDependencyComponent]].
     */
    public Reset(): StateDependencyComponent
    {
        this._stateCompletionManager.Reset();
        return this;
    }

    // #endregion
}
